import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_BASE_URL, getJsonAuthHeaders } from "../../../utils/api";
import "./AddCoursePage.css";

function AddCoursePage({ onLogout, showToast }) {
  const { campusId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("als_token");

  const [classes, setClasses] = useState([]);
  const [coursesByClass, setCoursesByClass] = useState(() => {
    try {
      const stored = localStorage.getItem("als_courses_by_class");
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [backendCourses, setBackendCourses] = useState([]);
  const [recommendedCoursesByClass, setRecommendedCoursesByClass] = useState({});

  // Persist coursesByClass to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(
        "als_courses_by_class",
        JSON.stringify(coursesByClass),
      );
    } catch (e) {
      console.error("Failed to save courses to localStorage", e);
    }
  }, [coursesByClass]);

  const fetchCourseCampusLinks = async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/course-campus?campus_id=${campusId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        const grouped = {};
        if (Array.isArray(data)) {
          data.forEach((link) => {
            const classIdKey = String(link.class_id);
            if (!grouped[classIdKey]) {
              grouped[classIdKey] = [];
            }
            grouped[classIdKey].push({
              id: link.id, // backend link id
              course_id: link.course_id,
              name: link.course_name || link.name,
              class_id: link.class_id,
              class_name: link.class_name || "",
              campus_id: link.campus_id,
            });
          });
        }
        setCoursesByClass(grouped);
      }
    } catch (err) {
      console.error("Failed to refresh course campus links", err);
    }
  };

  // Load classes, catalog courses, and campus courses for this campus (from API)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [classesRes, coursesRes, courseCampusRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/classes?campus_id=${campusId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/api/courses`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/api/course-campus?campus_id=${campusId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if ([classesRes, coursesRes, courseCampusRes].some((r) => r.status === 401)) {
          onLogout && onLogout();
          return;
        }

        if (!classesRes.ok) throw new Error("Failed to load classes");
        const classesData = await classesRes.json();
        const normalizedClasses = Array.isArray(classesData) ? classesData : [];
        setClasses(normalizedClasses);

        const coursesData = coursesRes.ok ? await coursesRes.json() : [];
        setBackendCourses(Array.isArray(coursesData) ? coursesData : []);

        const courseCampusData = courseCampusRes.ok ? await courseCampusRes.json() : [];
        
        // Group links by class_id
        const grouped = {};
        if (Array.isArray(courseCampusData)) {
          courseCampusData.forEach((link) => {
            const classIdKey = String(link.class_id);
            if (!grouped[classIdKey]) {
              grouped[classIdKey] = [];
            }
            grouped[classIdKey].push({
              id: link.id, // backend link id
              course_id: link.course_id,
              name: link.course_name || link.name,
              class_id: link.class_id,
              class_name: link.class_name || "",
              campus_id: link.campus_id,
            });
          });
        }
        setCoursesByClass(grouped);

        const recommendations = {};
        await Promise.all(
          normalizedClasses.map(async (cls) => {
            const recRes = await fetch(
              `${API_BASE_URL}/api/courses/recommended?class_id=${cls.id}`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            if (recRes.status === 401) {
              onLogout && onLogout();
              return;
            }
            recommendations[String(cls.id)] = recRes.ok ? await recRes.json() : [];
          }),
        );
        setRecommendedCoursesByClass(recommendations);
      } catch (err) {
        console.error("Failed to fetch classes/courses data", err);
        showToast &&
          showToast(
            "error",
            "Load Failed",
            "Could not load course/class data for this campus.",
          );
      } finally {
        setLoading(false);
      }
    };

    if (campusId && token) {
      fetchData();
    }
  }, [campusId, token, onLogout, showToast]);

  const toggleClassSelection = (classId) => {
    setSelectedClassIds((prev) => {
      const isSelected = prev.includes(classId);
      if (isSelected) {
        return prev.filter((id) => id !== classId);
      } else {
        return [...prev, classId];
      }
    });
    // Reset selected course when class selection changes
    setSelectedCourseId("");
  };

  const getRecommendedCoursesForSelection = () => {
    const byName = new Map();
    getSelectedClasses().forEach((cls) => {
      const recommended = recommendedCoursesByClass[String(cls.id)] || [];
      recommended.forEach((course) => {
        if (course?.name && !byName.has(course.name.toLowerCase())) {
          byName.set(course.name.toLowerCase(), {
            id: `recommended-${course.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
            name: course.name,
            code: course.code,
          });
        }
      });
    });

    return Array.from(byName.values());
  };

  // Merge backend recommendations and backend catalog courses to avoid duplication.
  const getMergedCourses = () => {
    const merged = [...getRecommendedCoursesForSelection()];
    backendCourses.forEach((bc) => {
      if (!merged.some((dc) => dc.name.toLowerCase() === bc.name.toLowerCase())) {
        merged.push({
          id: bc.id,
          name: bc.name,
          code: bc.code,
        });
      }
    });
    return merged;
  };

  const ensureBackendCourse = async (courseName, courseCode) => {
    const existingCourse = backendCourses.find(
      (bc) => bc.name.toLowerCase() === courseName.toLowerCase()
    );
    if (existingCourse) return existingCourse.id;

    const courseRes = await fetch(`${API_BASE_URL}/api/courses`, {
      method: "POST",
      headers: getJsonAuthHeaders(),
      body: JSON.stringify({
        name: courseName,
        code: courseCode,
        description: `School course for campus ${campusId}`,
      }),
    });

    if (courseRes.status === 401) {
      onLogout && onLogout();
      return null;
    }

    if (!courseRes.ok) {
      const refreshRes = await fetch(`${API_BASE_URL}/api/courses`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const refreshedCourses = refreshRes.ok ? await refreshRes.json() : [];
      if (Array.isArray(refreshedCourses)) {
        setBackendCourses(refreshedCourses);
        const refreshedMatch = refreshedCourses.find(
          (bc) => bc.name.toLowerCase() === courseName.toLowerCase(),
        );
        if (refreshedMatch) return refreshedMatch.id;
      }
      throw new Error(`Could not create ${courseName} in database.`);
    }

    const createdCourse = await courseRes.json();
    setBackendCourses((prev) => [...prev, createdCourse]);
    return createdCourse.id;
  };

  const linkCourseToClass = async (courseId, classId) => {
    const linkRes = await fetch(`${API_BASE_URL}/api/course-campus`, {
      method: "POST",
      headers: getJsonAuthHeaders(),
      body: JSON.stringify({
        course_id: courseId,
        campus_id: Number(campusId),
        class_id: classId,
      }),
    });

    if (linkRes.status === 401) {
      onLogout && onLogout();
      return "unauthorized";
    }
    if (linkRes.ok) return "added";
    if (linkRes.status === 400) return "duplicate";
    throw new Error("Failed to link course to class.");
  };

  const handleAddCourse = async (e) => {
    e.preventDefault();

    if (!selectedCourseId) {
      showToast &&
        showToast("error", "Missing Info", "Please select a course.");
      return;
    }

    if (selectedClassIds.length === 0) {
      showToast &&
        showToast(
          "error",
          "No Class Selected",
          "Please select at least one class.",
        );
      return;
    }

    const allAvailableCourseCatalog = getMergedCourses();
    const selectedCourse = allAvailableCourseCatalog.find(
      (c) => String(c.id) === String(selectedCourseId),
    );
    if (!selectedCourse) {
      showToast &&
        showToast("error", "Invalid Course", "Selected course not found.");
      return;
    }

    setSubmitting(true);
    try {
      // 1. Ensure the course exists in the backend catalog
      const resolvedCourseId = await ensureBackendCourse(
        selectedCourse.name,
        selectedCourse.code,
      );
      if (!resolvedCourseId) return;

      // 2. Link course to each selected class
      let addedCount = 0;
      let duplicateCount = 0;

      for (const classId of selectedClassIds) {
        const existing = getClassCourses(classId);
        if (existing.some((c) => c.name.toLowerCase() === selectedCourse.name.toLowerCase())) {
          duplicateCount++;
          continue;
        }

        const result = await linkCourseToClass(resolvedCourseId, classId);
        if (result === "unauthorized") return;
        if (result === "added") addedCount++;
        if (result === "duplicate") duplicateCount++;
      }

      setSelectedCourseId("");
      await fetchCourseCampusLinks();

      let message = `${selectedCourse.name} was added to ${addedCount} class${addedCount !== 1 ? "es" : ""}.`;
      if (duplicateCount > 0) {
        message += ` (${duplicateCount} class${duplicateCount !== 1 ? "es" : ""} already had this course)`;
      }

      showToast && showToast("success", "Course Added", message);
    } catch (err) {
      console.error(err);
      showToast &&
        showToast(
          "error",
          "Add Failed",
          err.message || "Could not add course to the selected classes.",
        );
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddRecommendedCourses = async () => {
    if (selectedClassIds.length === 0) {
      showToast &&
        showToast(
          "error",
          "No Class Selected",
          "Please select at least one class.",
        );
      return;
    }

    setSubmitting(true);
    try {
      let addedCount = 0;
      let duplicateCount = 0;

      for (const selectedClass of getSelectedClasses()) {
        const existingNames = new Set(
          getClassCourses(selectedClass.id).map((course) =>
            course.name.toLowerCase(),
          ),
        );

        const recommendedCourses =
          recommendedCoursesByClass[String(selectedClass.id)] || [];

        for (const recommendedCourse of recommendedCourses) {
          const courseName = recommendedCourse.name;
          if (!courseName) continue;

          if (existingNames.has(courseName.toLowerCase())) {
            duplicateCount++;
            continue;
          }

          const courseId = await ensureBackendCourse(
            courseName,
            recommendedCourse.code,
          );
          if (!courseId) return;

          const result = await linkCourseToClass(courseId, selectedClass.id);
          if (result === "unauthorized") return;
          if (result === "added") {
            addedCount++;
            existingNames.add(courseName.toLowerCase());
          }
          if (result === "duplicate") duplicateCount++;
        }
      }

      await fetchCourseCampusLinks();
      showToast &&
        showToast(
          "success",
          "Recommended Courses Added",
          `${addedCount} course link${addedCount !== 1 ? "s" : ""} added. ${duplicateCount} already existed.`,
        );
    } catch (err) {
      console.error(err);
      showToast &&
        showToast(
          "error",
          "Add Failed",
          err.message || "Could not add recommended courses.",
        );
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveCourse = async (classId, courseLinkId) => {
    const course = getClassCourses(classId).find((c) => c.id === courseLinkId);
    const courseName = course ? course.name : "This course";

    if (
      !window.confirm(
        `Are you sure you want to remove "${courseName}" from this class?`,
      )
    ) {
      return;
    }

    try {
      setSubmitting(true);
      const res = await fetch(`${API_BASE_URL}/api/course-campus/${courseLinkId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error("Failed to delete link");
      }
      showToast &&
        showToast("success", "Course Removed", "Course removed from this class.");
      
      await fetchCourseCampusLinks();
    } catch (err) {
      console.error("Failed to remove course link", err);
      showToast &&
        showToast("error", "Remove Failed", "Could not remove course from class.");
    } finally {
      setSubmitting(false);
    }
  };

  const getClassCourses = (classId) => {
    const key = String(classId);
    return coursesByClass[key] || [];
  };

  const isClassSelected = (classId) => selectedClassIds.includes(classId);

  const getSelectedClasses = () => {
    return classes.filter((c) => selectedClassIds.includes(c.id));
  };

  // Get courses that are not already assigned to any selected class
  const getAvailableCourses = () => {
    const allAssignedCourses = new Set();
    selectedClassIds.forEach((classId) => {
      const courses = getClassCourses(classId);
      courses.forEach((course) => allAssignedCourses.add(course.name.toLowerCase()));
    });

    const allCatalogCourses = getMergedCourses();
    return allCatalogCourses.filter(
      (course) => !allAssignedCourses.has(course.name.toLowerCase()),
    );
  };

  // Get the selected course name for display
  const getSelectedCourseName = () => {
    if (!selectedCourseId) return "";
    const allCatalogCourses = getMergedCourses();
    const course = allCatalogCourses.find(
      (c) => String(c.id) === String(selectedCourseId),
    );
    return course ? course.name : "";
  };

  if (loading) {
    return <div className="page-loading">Loading classes...</div>;
  }

  return (
    <div className="add-course-page">
      <div className="add-course-header">
        <button
          className="add-course-back"
          onClick={() => navigate(`/teachers/courses/campus/${campusId}`)}
        >
          ← Back
        </button>
        <h2>Add Course</h2>
      </div>

      <div className="add-course-card">
        {/* Classes Table */}
        <div className="classes-table-section">
          <div className="classes-table-title">
            Select Class{selectedClassIds.length !== 1 ? "es" : ""} to Add
            Courses ({selectedClassIds.length} selected)
          </div>

          {classes.length === 0 ? (
            <div className="classes-table-empty">
              No classes found for this campus.
            </div>
          ) : (
            <div className="classes-table-wrapper">
              <table className="classes-table">
                <thead>
                  <tr>
                    <th style={{ width: "40px" }}>Select</th>
                    <th style={{ width: "40px" }}>#</th>
                    <th style={{ textAlign: "left" }}>Class Name</th>
                    <th style={{ textAlign: "left" }}>Section</th>
                    <th style={{ textAlign: "center" }}>Courses</th>
                  </tr>
                </thead>
                <tbody>
                  {classes.map((cls, idx) => {
                    const courses = getClassCourses(cls.id);
                    const isSelected = isClassSelected(cls.id);

                    return (
                      <tr
                        key={cls.id}
                        className={isSelected ? "selected-row" : ""}
                        onClick={() => toggleClassSelection(cls.id)}
                      >
                        <td style={{ textAlign: "center" }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleClassSelection(cls.id)}
                            className="class-checkbox"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td>{idx + 1}</td>
                        <td style={{ textAlign: "left", fontWeight: 600 }}>
                          {cls.name}
                        </td>
                        <td
                          style={{
                            textAlign: "left",
                            color: "var(--ac-slate-light)",
                          }}
                        >
                          {cls.section || "—"}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <span className="course-count-badge">
                            {courses.length}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Add Course Form - Only Visible when at least one class is selected */}
        {selectedClassIds.length > 0 && (
          <form className="add-course-form" onSubmit={handleAddCourse}>
            <label htmlFor="course-select">
              Add Course to Selected Class
              {selectedClassIds.length !== 1 ? "es" : ""}
            </label>
            <div className="selected-classes-list">
              {getSelectedClasses().map((cls) => (
                <span key={cls.id} className="selected-class-tag">
                  {cls.name} {cls.section ? `— ${cls.section}` : ""}
                </span>
              ))}
            </div>
            <div className="add-course-form-row">
              <select
                id="course-select"
                value={selectedCourseId}
                onChange={(e) => setSelectedCourseId(e.target.value)}
                className="course-select-dropdown"
              >
                <option value="">Select a course...</option>
                {getAvailableCourses().map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.name}
                  </option>
                ))}
              </select>
              <button type="submit" disabled={submitting || !selectedCourseId}>
                {submitting ? "Adding…" : "Add Course"}
              </button>
              <button
                type="button"
                className="recommended-courses-btn"
                onClick={handleAddRecommendedCourses}
                disabled={submitting}
              >
                Add Recommended Courses
              </button>
            </div>
            {getAvailableCourses().length === 0 &&
              selectedClassIds.length > 0 && (
                <div className="no-available-courses-message">
                  All courses have been assigned to the selected classes.
                </div>
              )}
            {selectedCourseId && (
              <div className="selected-course-preview">
                <span className="selected-course-label">Selected:</span>
                <span className="selected-course-name">
                  {getSelectedCourseName()}
                </span>
              </div>
            )}
          </form>
        )}

        {/* Selected Classes Courses - Show for each selected class */}
        {selectedClassIds.length > 0 && (
          <div className="selected-classes-courses">
            <div className="selected-classes-header">
              <h3>Courses in Selected Classes</h3>
              <button
                className="clear-selection-btn"
                onClick={() => setSelectedClassIds([])}
              >
                ✕ Clear Selection
              </button>
            </div>

            {getSelectedClasses().map((selectedClass) => {
              const selectedClassCourses = getClassCourses(selectedClass.id);

              return (
                <div key={selectedClass.id} className="class-courses-section">
                  <div className="class-courses-title">
                    {selectedClass.name}{" "}
                    {selectedClass.section ? `— ${selectedClass.section}` : ""}
                    <span className="course-count-badge">
                      {selectedClassCourses.length}
                    </span>
                  </div>

                  {selectedClassCourses.length === 0 ? (
                    <div className="no-courses-message">
                      No courses have been added to this class yet. Use the form
                      above to add a course.
                    </div>
                  ) : (
                    <ul className="course-list">
                      {selectedClassCourses.map((course) => (
                        <li key={course.id}>
                          <span className="course-name">{course.name}</span>
                          <button
                            type="button"
                            className="remove-course-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveCourse(selectedClass.id, course.id);
                            }}
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default AddCoursePage;
